import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'firebase/auth';
import { ActualServer, Budget, ServerSetupFlow, NewServerForm, API_BASE_URL, CurrencyType, ServerConnectPrefill } from '../../types';
import CurrencyDropdown from '../CurrencyDropdown';
import { auth } from '../../utils/firebase';
import HostedServerPanel from '../HostedServerPanel';
import { isFeatureEnabled } from '../../utils/featureFlags';
import { useHostedServer } from '../../hooks/useHostedServer';

/**
 * Get the current user's ID token for API authentication
 */
async function getIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken();
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

interface ServerManagementModalProps {
  isVisible: boolean;
  onClose: () => void;
  user: User | null;
  savedServers: ActualServer[];
  activeServerId: string | null;
  onSaveServers: (servers: ActualServer[]) => void;
  onSetActiveServer: (serverId: string) => void;
  onRemoveServer: (serverId: string) => void;
  onRemoveBudgetPassword: (serverId: string, budgetId: string) => void;
  onLoadBudgetsForServer: (server: ActualServer, forceRefresh?: boolean) => Promise<Budget[]>;
  onValidateBudgetAccess: (server: ActualServer, budgetId: string, budgetPassword?: string) => Promise<{ 
    success: boolean; 
    needsPassword: boolean; 
    error?: string 
  }>;
  connectPrefill?: ServerConnectPrefill | null;
  /** When true (guided onboarding step 3), user must pick a default budget before the flow completes */
  requireDefaultBudget?: boolean;
}

function normalizeServerUrl(url: string): string {
  return url.trim().replace(/\/$/, '').toLowerCase();
}

function getManagedServerDisplayName(
  hostedName: string | null | undefined,
  fallback: string
): string {
  const trimmed = hostedName?.trim();
  return trimmed || fallback;
}

const ServerManagementModal: React.FC<ServerManagementModalProps> = ({
  isVisible,
  onClose,
  user,
  savedServers,
  activeServerId,
  onSaveServers,
  onSetActiveServer,
  onRemoveServer,
  onRemoveBudgetPassword,
  onLoadBudgetsForServer,
  onValidateBudgetAccess,
  connectPrefill = null,
  requireDefaultBudget = false
}) => {
  const { t } = useTranslation();
  const { server: hostedServer } = useHostedServer(user);
  
  // Helper function to handle validation error display
  const getValidationErrorText = (error: string) => {
    // If it looks like a translation key, translate it
    if (error.startsWith('serverManagement.')) {
      const key = error.replace('serverManagement.', '');
      return t(key, { ns: 'serverManagement' });
    }
    // Otherwise, return the error as-is
    return error;
  };

  const [selectedServerForManagement, setSelectedServerForManagement] = useState<string | null>(
    activeServerId || (savedServers.length > 0 ? savedServers[0].id : null)
  );

  // Ensure selectedServerForManagement is set when savedServers or activeServerId changes
  React.useEffect(() => {
    if (!selectedServerForManagement && savedServers.length > 0) {
      setSelectedServerForManagement(activeServerId || savedServers[0].id);
    } else if (activeServerId && selectedServerForManagement !== activeServerId) {
      setSelectedServerForManagement(activeServerId);
    }
  }, [savedServers, activeServerId, selectedServerForManagement]);
  const [managementView, setManagementView] = useState<'overview' | 'add-server' | 'change-budget'>('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // New server form state
  const [newServerForm, setNewServerForm] = useState<NewServerForm>({
    name: '',
    url: '',
    password: ''
  });

  // Server setup flow state
  const [serverSetupFlow, setServerSetupFlow] = useState<ServerSetupFlow>({
    step: 'details',
    budgets: []
  });
  const prefillAppliedRef = React.useRef(false);

  const startChangeBudget = React.useCallback(async (server: ActualServer) => {
    setManagementView('change-budget');
    setServerSetupFlow({
      step: 'budget',
      budgets: [],
      selectedBudgetId: server.defaultBudgetId,
      selectedBudgetCurrency: server.defaultBudgetCurrency || 'USD'
    });

    setIsLoading(true);
    try {
      const budgets = await onLoadBudgetsForServer(server);
      if (budgets.length === 0) {
        setServerSetupFlow({ step: 'no-budgets', budgets: [] });
        return;
      }

      setServerSetupFlow(prev => ({
        ...prev,
        step: 'budget',
        budgets,
        selectedBudgetId: server.defaultBudgetId || budgets[0]?.id,
        selectedBudgetCurrency:
          server.defaultBudgetCurrency ||
          budgets.find(b => b.id === (server.defaultBudgetId || budgets[0]?.id))?.currency ||
          'USD'
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToLoadBudgets', { ns: 'serverManagement' }));
    } finally {
      setIsLoading(false);
    }
  }, [onLoadBudgetsForServer, t]);

  React.useEffect(() => {
    if (!isVisible) {
      prefillAppliedRef.current = false;
      return;
    }
    if (!connectPrefill?.url || prefillAppliedRef.current) {
      return;
    }

    prefillAppliedRef.current = true;
    const normalizedPrefill = normalizeServerUrl(connectPrefill.url);
    const existingServer = savedServers.find(
      server => normalizeServerUrl(server.url) === normalizedPrefill
    );

    if (existingServer) {
      setSelectedServerForManagement(existingServer.id);
      onSetActiveServer(existingServer.id);
      if (!existingServer.defaultBudgetId) {
        startChangeBudget(existingServer);
      } else {
        setManagementView('overview');
      }
      return;
    }

    setManagementView('add-server');
    setNewServerForm({
      name: connectPrefill.name,
      url: connectPrefill.url,
      password: ''
    });
    setServerSetupFlow({ step: 'details', budgets: [] });
    setError(null);
  }, [isVisible, connectPrefill, savedServers, onSetActiveServer, startChangeBudget]);

  if (!isVisible) return null;

  const isHostedConnect = Boolean(connectPrefill?.url);
  const mustSelectDefaultBudget = requireDefaultBudget || isHostedConnect;
  const managedServerFallbackName = t('hosted.defaultServerName', { ns: 'serverManagement' });

  const getHostedManagedName = () =>
    getManagedServerDisplayName(hostedServer?.name, managedServerFallbackName);

  const serverNeedsManagedReset = (server: ActualServer): boolean => {
    if (!hostedServer?.serverUrl) return false;
    const isManagedEntry = Boolean(server.hostedByNimah) || savedServers.length === 1;
    if (!isManagedEntry) return false;

    const managedName = getHostedManagedName();
    const urlMismatch =
      normalizeServerUrl(server.url) !== normalizeServerUrl(hostedServer.serverUrl);
    const nameMismatch = server.name !== managedName;
    return urlMismatch || nameMismatch;
  };

  const handleResetToManagedServer = (server: ActualServer) => {
    if (!hostedServer?.serverUrl) return;

    const managedName = getHostedManagedName();
    const urlChanged =
      normalizeServerUrl(server.url) !== normalizeServerUrl(hostedServer.serverUrl);

    const updatedServers = savedServers.map(s => {
      if (s.id !== server.id) return s;

      const nextServer: ActualServer = {
        ...s,
        url: hostedServer.serverUrl,
        name: managedName,
        hostedByNimah: true
      };

      if (urlChanged) {
        delete nextServer.defaultBudgetId;
        delete nextServer.defaultBudgetName;
        delete nextServer.defaultBudgetPassword;
        delete nextServer.defaultBudgetCurrency;
        delete nextServer.cachedBudgets;
        delete nextServer.budgetsCachedAt;
        delete nextServer.cachedAccounts;
        delete nextServer.accountsCachedAt;
        delete nextServer.lastUsedBudgetId;
        delete nextServer.lastUsedAccountId;
      }

      return nextServer;
    });

    onSaveServers(updatedServers);
    setError(null);
  };

  const closeModal = () => {
    onClose();
    setManagementView('overview');
    setServerSetupFlow({ step: 'details', budgets: [] });
    setNewServerForm({ name: '', url: '', password: '' });
    setError(null);
  };

  const refreshBudgetsForLinkedServer = async (linkedServer: ActualServer) => {
    setIsLoading(true);
    setError(null);
    try {
      const budgets = await onLoadBudgetsForServer(linkedServer, true);
      if (budgets.length > 0) {
        setManagementView('change-budget');
        setServerSetupFlow({
          step: 'budget',
          budgets,
          selectedBudgetId: budgets[0]?.id,
          selectedBudgetCurrency: budgets[0]?.currency || 'USD'
        });
      } else {
        setServerSetupFlow({ step: 'no-budgets', budgets: [] });
        setError(t('connectNoBudgetsStillEmpty', { ns: 'serverManagement' }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToLoadBudgets', { ns: 'serverManagement' }));
    } finally {
      setIsLoading(false);
    }
  };

  const renderNoBudgetsSection = (actualUrl?: string, linkedServer?: ActualServer | null) => (
    <div className="no-budgets-section">
      <div className="info-box">
        <p>{t('connectNoBudgetsDescription', { ns: 'serverManagement' })}</p>
        <ol className="no-budgets-steps">
          <li>{t('connectNoBudgetsStepOpen', { ns: 'serverManagement' })}</li>
          <li>{t('connectNoBudgetsStepStartFresh', { ns: 'serverManagement' })}</li>
          <li>{t('connectNoBudgetsStepReturn', { ns: 'serverManagement' })}</li>
        </ol>
        {actualUrl && (
          <p className="step-note">
            <a href={actualUrl} target="_blank" rel="noopener noreferrer">
              {t('connectNoBudgetsOpenActual', { ns: 'serverManagement' })}
            </a>
          </p>
        )}
      </div>
      <div className="setup-actions">
        {linkedServer && (
          <button
            onClick={() => refreshBudgetsForLinkedServer(linkedServer)}
            className="select-budget-button"
            disabled={isLoading}
          >
            {isLoading ? t('connecting', { ns: 'serverManagement' }) : t('connectNoBudgetsRetry', { ns: 'serverManagement' })}
          </button>
        )}
        <button onClick={closeModal} className="cancel-button">
          {t('cancel', { ns: 'serverManagement' })}
        </button>
      </div>
    </div>
  );

  const handleCreateServer = async () => {
    if (!newServerForm.name.trim() || !newServerForm.url.trim() || !newServerForm.password.trim()) {
      setError(t('allFieldsRequired', { ns: 'serverManagement' }));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get authentication token
      const token = await getIdToken();
      if (!token) {
        throw new Error(t('authRequiredForServerManagement', { ns: 'auth' }));
      }

      // Test connection first
      let response;
      try {
        response = await fetch(`${API_BASE_URL}/auth-and-get-budgets`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            serverURL: newServerForm.url,
            password: newServerForm.password
          }),
        });
      } catch (networkError) {
        throw new Error(`${t('networkError', { ns: 'serverManagement' })}: ${networkError instanceof Error ? networkError.message : t('failedToConnect', { ns: 'serverManagement' })}`);
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        throw new Error(errorData.error || t('failedToAuthenticate', { ns: 'serverManagement' }));
      }

      const data = await response.json();
      
      // Move to budget selection if budgets are available
      if (data.budgets && data.budgets.length > 0) {
        setServerSetupFlow({
          step: 'budget',
          budgets: data.budgets,
          selectedBudgetId: data.budgets[0]?.id,
          selectedBudgetCurrency: data.budgets[0]?.currency || 'USD',
          validationError: undefined
        });
      } else {
        const newServer: ActualServer = {
          id: Date.now().toString(),
          name: newServerForm.name,
          url: newServerForm.url,
          password: newServerForm.password,
          cachedBudgets: data.budgets || [],
          budgetsCachedAt: Date.now(),
          ...(isHostedConnect ? { hostedByNimah: true } : {})
        };

        const updatedServers = [...savedServers, newServer];
        onSaveServers(updatedServers);
        onSetActiveServer(newServer.id);

        if (mustSelectDefaultBudget) {
          setSelectedServerForManagement(newServer.id);
          setServerSetupFlow({ step: 'no-budgets', budgets: [] });
          return;
        }

        setNewServerForm({ name: '', url: '', password: '' });
        setServerSetupFlow({ step: 'details', budgets: [] });
        setManagementView('overview');
        closeModal();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToConnect', { ns: 'serverManagement' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleServerSetupBudgetSelection = async (budgetId: string, budgetPassword?: string) => {
    setServerSetupFlow(prev => ({ ...prev, step: 'validating', selectedBudgetId: budgetId, validationError: undefined }));
    setIsLoading(true);
    setError(null);

    try {
      // Create a temporary server object for validation
      const tempServer: ActualServer = {
        id: 'temp',
        name: newServerForm.name,
        url: newServerForm.url,
        password: newServerForm.password
      };

      const validation = await onValidateBudgetAccess(tempServer, budgetId, budgetPassword);
      
      if (!validation.success) {
        if (validation.needsPassword) {
          setServerSetupFlow(prev => ({ 
            ...prev, 
            step: 'budget-password',
            selectedBudgetId: budgetId,
            needsBudgetPassword: true,
            validationError: 'serverManagement.budgetEncryptedPasswordRequired'
          }));
          return;
        } else {
          setServerSetupFlow(prev => ({ 
            ...prev, 
            step: 'budget',
            validationError: validation.error || 'serverManagement.failedToAccessBudget'
          }));
          return;
        }
      }

      // Budget access successful - create server
      const selectedBudget = serverSetupFlow.budgets.find(b => b.id === budgetId);
      const newServer: ActualServer = {
        id: Date.now().toString(),
        name: newServerForm.name,
        url: newServerForm.url,
        password: newServerForm.password,
        defaultBudgetId: budgetId,
        defaultBudgetName: selectedBudget?.name,
        defaultBudgetPassword: budgetPassword,
        defaultBudgetCurrency: serverSetupFlow.selectedBudgetCurrency,
        cachedBudgets: serverSetupFlow.budgets,
        budgetsCachedAt: Date.now(),
        ...(isHostedConnect ? { hostedByNimah: true } : {})
      };

      // Save budget password if provided
      if (budgetPassword) {
        newServer.budgetPasswords = {
          [budgetId]: budgetPassword
        };
      }
      
      const updatedServers = [...savedServers, newServer];
      onSaveServers(updatedServers);
      onSetActiveServer(newServer.id);

      // Reset form and close modal
      setNewServerForm({ name: '', url: '', password: '' });
      setServerSetupFlow({ step: 'details', budgets: [] });
      setManagementView('overview');
      closeModal();
    } catch (err) {
      setServerSetupFlow(prev => ({ 
        ...prev, 
        step: 'budget',
        validationError: err instanceof Error ? err.message : 'serverManagement.failedToValidateBudgetAccess'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const updateServerDefaultBudget = async (server: ActualServer, budgetId: string, budgetPassword?: string) => {
    const selectedBudget = serverSetupFlow.budgets.find(b => b.id === budgetId);
    
    // First validate budget access
    setServerSetupFlow(prev => ({ ...prev, step: 'validating', validationError: undefined }));
    setIsLoading(true);
    setError(null);

    try {
      // Use provided password or try saved password for this budget
      const savedBudgetPassword = server.budgetPasswords?.[budgetId];
      const passwordToUse = budgetPassword || savedBudgetPassword;

      const validation = await onValidateBudgetAccess(server, budgetId, passwordToUse);
      
      if (!validation.success) {
        if (validation.needsPassword) {
          setServerSetupFlow(prev => ({ 
            ...prev, 
            step: 'budget-password',
            needsBudgetPassword: true,
            validationError: 'serverManagement.budgetEncryptedPasswordRequired'
          }));
          return;
        } else {
          setServerSetupFlow(prev => ({ 
            ...prev, 
            step: 'budget',
            validationError: validation.error || 'serverManagement.failedToAccessBudget'
          }));
          return;
        }
      }

      // Validation successful - update server
      const updatedServers = savedServers.map(s => {
        if (s.id === server.id) {
          const updatedServer = {
            ...s,
            defaultBudgetId: budgetId,
            defaultBudgetName: selectedBudget?.name,
            defaultBudgetPassword: passwordToUse,
            defaultBudgetCurrency: serverSetupFlow.selectedBudgetCurrency || selectedBudget?.currency
          };

          // Save budget password if provided
          if (passwordToUse) {
            updatedServer.budgetPasswords = {
              ...s.budgetPasswords,
              [budgetId]: passwordToUse
            };
          }

          return updatedServer;
        }
        return s;
      });
      
      onSaveServers(updatedServers);

      setServerSetupFlow({ step: 'details', budgets: [] });
      if (mustSelectDefaultBudget) {
        closeModal();
        return;
      }
      setManagementView('overview');
    } catch (err) {
      setServerSetupFlow(prev => ({ 
        ...prev, 
        step: 'budget',
        validationError: err instanceof Error ? err.message : 'serverManagement.failedToValidateBudgetAccess'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content server-management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('title', { ns: 'serverManagement' })}</h3>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        
        <div className="modal-body">
          {managementView === 'overview' && (
            <div className="server-management-overview">
              {isFeatureEnabled('ENABLE_ACTUAL_HOSTING') && (
                <HostedServerPanel user={user} />
              )}

              {savedServers.length > 0 ? (
                <>
                  <div className="server-selector">
                    <div className="server-dropdown-section">
                      <label htmlFor="server-select">{t('selectServer', { ns: 'serverManagement' })}</label>
                      <div className="server-dropdown-container">
                        <select 
                          id="server-select"
                          value={selectedServerForManagement || ''}
                          onChange={(e) => setSelectedServerForManagement(e.target.value)}
                          className="server-dropdown"
                        >
                          {savedServers.map(server => (
                            <option key={server.id} value={server.id}>
                              {server.name} {server.id === activeServerId ? `(${t('activeServer', { ns: 'serverManagement' })})` : ''}
                            </option>
                          ))}
                        </select>
                        <button 
                          onClick={() => {
                            setManagementView('add-server');
                            setNewServerForm({ name: '', url: '', password: '' });
                          }}
                          className="add-server-button"
                        >
                          {t('addNewServer', { ns: 'serverManagement' })}
                        </button>
                      </div>
                    </div>
                  </div>

                  {selectedServerForManagement && (() => {
                    const selectedServer = savedServers.find(s => s.id === selectedServerForManagement);
                    if (!selectedServer) return null;
                    
                    return (
                      <div className="selected-server-details">
                        <h4>{t('serverDetails', { ns: 'serverManagement' })}</h4>
                        <div className="server-info-card">
                          <div className="server-basic-info">
                            <strong>{selectedServer.name}</strong>
                            <span>{selectedServer.url}</span>
                            {selectedServer.defaultBudgetName && (
                              <small className="default-budget-info">
                                {t('defaultBudget', { ns: 'serverManagement' })} {selectedServer.defaultBudgetName}
                                {selectedServer.defaultBudgetCurrency && (
                                  <> ({selectedServer.defaultBudgetCurrency})</>
                                )}
                              </small>
                            )}
                            {selectedServer.id === activeServerId && <span className="active-badge">{t('activeServer', { ns: 'serverManagement' })}</span>}
                          </div>

                          {serverNeedsManagedReset(selectedServer) && (
                            <div className="managed-server-reset">
                              <p className="step-note">{t('hosted.resetToManagedServerHint', { ns: 'serverManagement' })}</p>
                              <button
                                type="button"
                                onClick={() => handleResetToManagedServer(selectedServer)}
                                className="action-button secondary"
                              >
                                {t('hosted.resetToManagedServer', { ns: 'serverManagement' })}
                              </button>
                            </div>
                          )}
                          
                          <div className="server-actions-grid">
                            {selectedServer.id !== activeServerId && (
                              <button 
                                onClick={() => onSetActiveServer(selectedServer.id)}
                                className="action-button primary"
                              >
                                {t('setAsActive', { ns: 'serverManagement' })}
                              </button>
                            )}
                            
                            <button 
                              onClick={() => startChangeBudget(selectedServer)}
                              className="action-button secondary"
                            >
                                                              {t('changeDefaultBudget', { ns: 'serverManagement' })}
                            </button>
                            
                            {selectedServer.budgetPasswords && Object.keys(selectedServer.budgetPasswords).length > 0 && (
                              <button 
                                onClick={() => {
                                  Object.keys(selectedServer.budgetPasswords!).forEach(budgetId => 
                                    onRemoveBudgetPassword(selectedServer.id, budgetId)
                                  );
                                }}
                                className="action-button warning"
                              >
                                {t('clearSavedPasswords', { ns: 'serverManagement' })}
                              </button>
                            )}
                            
                            <button 
                              onClick={() => onRemoveServer(selectedServer.id)}
                              className="action-button danger"
                            >
                                                              {t('removeServer', { ns: 'serverManagement' })}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="no-servers-prompt">
                  <h4>{t('noServersConnected', { ns: 'serverManagement' })}</h4>
                  <p>{t('noServersDescription', { ns: 'serverManagement' })}</p>
                  <button 
                    onClick={() => {
                      setManagementView('add-server');
                      setNewServerForm({ name: '', url: '', password: '' });
                    }}
                    className="add-first-server-button"
                  >
                    {t('addFirstServer', { ns: 'serverManagement' })}
                  </button>
                </div>
              )}
            </div>
          )}

          {managementView === 'add-server' && (
            <div className="add-server-section">
              <div className="section-header">
                <h4>
                  {isHostedConnect
                    ? t('connectHostedServerTitle', { ns: 'serverManagement' })
                    : t('addNewServerTitle', { ns: 'serverManagement' })}
                </h4>
                {savedServers.length > 0 && !isHostedConnect && (
                  <button 
                    onClick={() => setManagementView('overview')}
                    className="back-to-overview-button"
                  >
                    {t('backToOverview', { ns: 'serverManagement' })}
                  </button>
                )}
              </div>
              
              {serverSetupFlow.step === 'details' && (
                <>
                  <div className="form-group">
                    <label>{t('serverName', { ns: 'serverManagement' })}</label>
                    <input
                      type="text"
                      value={newServerForm.name}
                      onChange={(e) => setNewServerForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('serverNamePlaceholder', { ns: 'serverManagement' })}
                      autoFocus={!isHostedConnect}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('serverUrl', { ns: 'serverManagement' })}</label>
                    <input
                      type="url"
                      value={newServerForm.url}
                      onChange={(e) => setNewServerForm(prev => ({ ...prev, url: e.target.value }))}
                      placeholder={t('serverUrlPlaceholder', { ns: 'serverManagement' })}
                      readOnly={isHostedConnect}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('password', { ns: 'serverManagement' })}</label>
                    <input
                      type="password"
                      value={newServerForm.password}
                      onChange={(e) => setNewServerForm(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={t('passwordPlaceholder', { ns: 'serverManagement' })}
                      autoFocus={isHostedConnect}
                    />
                    <p className="password-security-notice">{t('passwordSecurityNotice', { ns: 'common' })}</p>
                  </div>
                  
                  <div className="form-actions">
                    {(savedServers.length > 0 || isHostedConnect) && (
                      <button 
                        onClick={() => (isHostedConnect ? closeModal() : setManagementView('overview'))}
                        className="cancel-button"
                      >
                        {t('cancel', { ns: 'serverManagement' })}
                      </button>
                    )}
                    <button 
                      onClick={handleCreateServer}
                      className="create-server-button"
                      disabled={isLoading}
                    >
                      {isLoading ? t('connecting', { ns: 'serverManagement' }) : t('connectAndContinue', { ns: 'serverManagement' })}
                    </button>
                  </div>
                </>
              )}

              {serverSetupFlow.step === 'budget' && (
                <>
                  <p>{t('selectDefaultBudget', { ns: 'serverManagement' })} <strong>{newServerForm.name}</strong></p>
                  
                  {serverSetupFlow.validationError && (
                    <div className="error budget-validation-error">
                      <p>{getValidationErrorText(serverSetupFlow.validationError)}</p>
                    </div>
                  )}
                  
                  <div className="budget-list">
                    {serverSetupFlow.budgets.map(budget => (
                      <div 
                        key={budget.id} 
                        className={`budget-item ${serverSetupFlow.selectedBudgetId === budget.id ? 'selected' : ''}`}
                        onClick={() => setServerSetupFlow(prev => ({ 
                          ...prev, 
                          selectedBudgetId: budget.id, 
                          selectedBudgetCurrency: budget.currency || 'USD',
                          validationError: undefined 
                        }))}
                      >
                        <span>{budget.name}</span>
                        {budget.currency && <span className="budget-currency">({budget.currency})</span>}
                      </div>
                    ))}
                  </div>
                  
                  <div className="setup-actions">
                    <button 
                      onClick={() => setServerSetupFlow({ step: 'details', budgets: [] })}
                      className="back-button"
                    >
                      {t('backToServerDetails', { ns: 'serverManagement' })}
                    </button>
                    <button 
                      onClick={() => {
                        if (serverSetupFlow.selectedBudgetId) {
                          setServerSetupFlow(prev => ({
                            ...prev,
                            step: 'budget-currency',
                            selectedBudgetCurrency: prev.selectedBudgetCurrency || prev.budgets.find(b => b.id === prev.selectedBudgetId)?.currency || 'USD'
                          }));
                        }
                      }}
                      disabled={!serverSetupFlow.selectedBudgetId || isLoading}
                      className="select-budget-button"
                    >
                      {t('continue', { ns: 'serverManagement' })}
                    </button>
                    {!mustSelectDefaultBudget && (
                      <button 
                        onClick={() => {
                          const newServer: ActualServer = {
                            id: Date.now().toString(),
                            name: newServerForm.name,
                            url: newServerForm.url,
                            password: newServerForm.password,
                            cachedBudgets: serverSetupFlow.budgets,
                            budgetsCachedAt: Date.now(),
                            ...(isHostedConnect ? { hostedByNimah: true } : {})
                          };
                          
                          const updatedServers = [...savedServers, newServer];
                          onSaveServers(updatedServers);
                          onSetActiveServer(newServer.id);

                          setNewServerForm({ name: '', url: '', password: '' });
                          setServerSetupFlow({ step: 'details', budgets: [] });
                          setManagementView('overview');
                          closeModal();
                        }}
                        className="skip-budget-button"
                      >
                        {t('skipCreateServer', { ns: 'serverManagement' })}
                      </button>
                    )}
                  </div>
                </>
              )}

              {serverSetupFlow.step === 'budget-currency' && (
                <>
                  <p>{t('selectBudgetCurrency', { ns: 'serverManagement' })} <strong>{serverSetupFlow.budgets.find(b => b.id === serverSetupFlow.selectedBudgetId)?.name}</strong></p>
                  
                  <CurrencyDropdown
                    value={(serverSetupFlow.selectedBudgetCurrency || 'USD') as CurrencyType}
                    onChange={(currency) => setServerSetupFlow(prev => ({ ...prev, selectedBudgetCurrency: currency }))}
                    label={t('budgetCurrency', { ns: 'serverManagement' })}
                    id="budget-currency"
                  />
                  
                  <div className="setup-actions">
                    <button 
                      onClick={() => setServerSetupFlow(prev => ({ ...prev, step: 'budget' }))}
                      className="back-button"
                    >
                      {t('backToBudgets', { ns: 'serverManagement' })}
                    </button>
                    <button 
                      onClick={() => {
                        if (serverSetupFlow.selectedBudgetId && serverSetupFlow.selectedBudgetCurrency) {
                          handleServerSetupBudgetSelection(serverSetupFlow.selectedBudgetId);
                        }
                      }}
                      disabled={!serverSetupFlow.selectedBudgetCurrency || isLoading}
                      className="select-budget-button"
                    >
                      {isLoading ? t('validating', { ns: 'serverManagement' }) : t('createServerWithBudget', { ns: 'serverManagement' })}
                    </button>
                  </div>
                </>
              )}

              {serverSetupFlow.step === 'budget-password' && (
                <div className="budget-password-section">
                  <div className="password-prompt">
                    <h5>{t('budgetPasswordRequired', { ns: 'serverManagement' })}</h5>
                    <p>{t('budgetLabel', { ns: 'serverManagement' })} <strong>{serverSetupFlow.budgets.find(b => b.id === serverSetupFlow.selectedBudgetId)?.name}</strong></p>
                    
                    {serverSetupFlow.validationError && (
                      <div className="error">
                        <p>{getValidationErrorText(serverSetupFlow.validationError)}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>{t('budgetPassword', { ns: 'serverManagement' })}</label>
                    <input
                      type="password"
                      value={serverSetupFlow.budgetPassword || ''}
                      onChange={(e) => setServerSetupFlow(prev => ({ ...prev, budgetPassword: e.target.value, validationError: undefined }))}
                      placeholder={t('budgetPasswordPlaceholder', { ns: 'serverManagement' })}
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && serverSetupFlow.selectedBudgetId && serverSetupFlow.budgetPassword) {
                          handleServerSetupBudgetSelection(serverSetupFlow.selectedBudgetId, serverSetupFlow.budgetPassword);
                        }
                      }}
                    />
                    <p className="password-security-notice">{t('passwordSecurityNotice', { ns: 'common' })}</p>
                  </div>
                  
                  <div className="setup-actions">
                    <button 
                      onClick={() => setServerSetupFlow(prev => ({ ...prev, step: 'budget', needsBudgetPassword: false, budgetPassword: '', validationError: undefined }))}
                      className="back-button"
                    >
                      {t('backToBudgets', { ns: 'serverManagement' })}
                    </button>
                    <button 
                      onClick={() => {
                        if (serverSetupFlow.selectedBudgetId && serverSetupFlow.budgetPassword) {
                          handleServerSetupBudgetSelection(serverSetupFlow.selectedBudgetId, serverSetupFlow.budgetPassword);
                        }
                      }}
                      disabled={!serverSetupFlow.budgetPassword || isLoading}
                      className="unlock-budget-button"
                    >
                      {isLoading ? t('validating', { ns: 'serverManagement' }) : t('createServerWithPassword', { ns: 'serverManagement' })}
                    </button>
                  </div>
                </div>
              )}

              {serverSetupFlow.step === 'no-budgets' && renderNoBudgetsSection(
                newServerForm.url || undefined,
                savedServers.find(s => s.id === selectedServerForManagement) ?? null
              )}

              {serverSetupFlow.step === 'validating' && (
                <div className="budget-validation-section">
                  <div className="validating-state">
                    <div className="spinner"></div>
                    <h5>{t('validatingBudgetAccess', { ns: 'serverManagement' })}</h5>
                    <p>{t('testingAccess', { ns: 'serverManagement' })} <strong>{serverSetupFlow.budgets.find(b => b.id === serverSetupFlow.selectedBudgetId)?.name}</strong></p>
                    <p>{t('verifyingBudget', { ns: 'serverManagement' })}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {managementView === 'change-budget' && (() => {
            const linkedServer = savedServers.find(s => s.id === selectedServerForManagement) ?? null;
            const actualUrl = connectPrefill?.url || linkedServer?.url;

            return (
            <div className="budget-selection-area">
              <div className="section-header">
                <h4>
                  {serverSetupFlow.step === 'no-budgets'
                    ? t('connectNoBudgetsTitle', { ns: 'serverManagement' })
                    : t('chooseDefaultBudget', { ns: 'serverManagement' })}
                </h4>
                <button 
                  onClick={() => (mustSelectDefaultBudget ? closeModal() : (setManagementView('overview'), setServerSetupFlow({ step: 'details', budgets: [] })))}
                  className="back-to-overview-button"
                >
                  {mustSelectDefaultBudget
                    ? t('cancel', { ns: 'serverManagement' })
                    : t('backToOverview', { ns: 'serverManagement' })}
                </button>
              </div>

              {serverSetupFlow.step === 'no-budgets' ? (
                renderNoBudgetsSection(actualUrl, linkedServer)
              ) : (
              <>
              <p>{t('selectDefaultBudget', { ns: 'serverManagement' })} <strong>{linkedServer?.name}</strong></p>
              
              {serverSetupFlow.step === 'budget' && (
                <>
                  {serverSetupFlow.validationError && (
                    <div className="error budget-validation-error">
                      <p>{getValidationErrorText(serverSetupFlow.validationError)}</p>
                    </div>
                  )}
                  
                  <div className="budget-list">
                    {isLoading && serverSetupFlow.budgets.length === 0 ? (
                      <div className="loading-budgets">
                        <div className="spinner"></div>
                        <p>{t('loading', { ns: 'import' })}</p>
                      </div>
                    ) : serverSetupFlow.budgets.length === 0 ? (
                      renderNoBudgetsSection(actualUrl, linkedServer)
                    ) : (
                      serverSetupFlow.budgets.map(budget => (
                        <div 
                          key={budget.id} 
                          className={`budget-item ${serverSetupFlow.selectedBudgetId === budget.id ? 'selected' : ''}`}
                          onClick={() => setServerSetupFlow(prev => ({ 
                            ...prev, 
                            selectedBudgetId: budget.id, 
                            selectedBudgetCurrency: budget.currency || 'USD',
                            validationError: undefined 
                          }))}
                        >
                          <span>{budget.name}</span>
                          {budget.currency && <span className="budget-currency">({budget.currency})</span>}
                          {savedServers.find(s => s.id === selectedServerForManagement)?.budgetPasswords?.[budget.id] && (
                            <small className="password-saved">{t('passwordSaved', { ns: 'serverManagement' })}</small>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  
                  {serverSetupFlow.budgets.length > 0 && (
                  <div className="setup-actions">
                    <button 
                      onClick={() => {
                        if (mustSelectDefaultBudget) {
                          closeModal();
                          return;
                        }
                        setManagementView('overview');
                        setServerSetupFlow({ step: 'details', budgets: [] });
                      }}
                      className="back-button"
                    >
                      {t('cancel', { ns: 'serverManagement' })}
                    </button>
                    <button 
                      onClick={() => {
                        if (serverSetupFlow.selectedBudgetId) {
                          setServerSetupFlow(prev => ({
                            ...prev,
                            step: 'budget-currency',
                            selectedBudgetCurrency: prev.selectedBudgetCurrency || prev.budgets.find(b => b.id === prev.selectedBudgetId)?.currency || 'USD'
                          }));
                        }
                      }}
                      disabled={!serverSetupFlow.selectedBudgetId || isLoading}
                      className="select-budget-button"
                    >
                      {t('continue', { ns: 'serverManagement' })}
                    </button>
                  </div>
                  )}
                </>
              )}

              {serverSetupFlow.step === 'budget-currency' && (
                <>
                  <p>{t('selectBudgetCurrency', { ns: 'serverManagement' })} <strong>{serverSetupFlow.budgets.find(b => b.id === serverSetupFlow.selectedBudgetId)?.name}</strong></p>
                  
                  <CurrencyDropdown
                    value={(serverSetupFlow.selectedBudgetCurrency || 'USD') as CurrencyType}
                    onChange={(currency) => setServerSetupFlow(prev => ({ ...prev, selectedBudgetCurrency: currency }))}
                    label={t('budgetCurrency', { ns: 'serverManagement' })}
                    id="budget-currency-change"
                  />
                  
                  <div className="setup-actions">
                    <button 
                      onClick={() => setServerSetupFlow(prev => ({ ...prev, step: 'budget' }))}
                      className="back-button"
                    >
                      {t('backToBudgets', { ns: 'serverManagement' })}
                    </button>
                    <button 
                      onClick={() => {
                        if (serverSetupFlow.selectedBudgetId && serverSetupFlow.selectedBudgetCurrency && selectedServerForManagement) {
                          const selectedServer = savedServers.find(s => s.id === selectedServerForManagement);
                          if (selectedServer) {
                            updateServerDefaultBudget(selectedServer, serverSetupFlow.selectedBudgetId);
                          }
                        }
                      }}
                      disabled={!serverSetupFlow.selectedBudgetCurrency || isLoading}
                      className="select-budget-button"
                    >
                      {isLoading ? t('validating', { ns: 'serverManagement' }) : t('setAsDefault', { ns: 'serverManagement' })}
                    </button>
                  </div>
                </>
              )}

              {serverSetupFlow.step === 'budget-password' && (
                <div className="budget-password-section">
                  <div className="password-prompt">
                    <h5>{t('budgetPasswordRequired', { ns: 'serverManagement' })}</h5>
                    <p>{t('budget', { ns: 'import' })} <strong>{serverSetupFlow.budgets.find(b => b.id === serverSetupFlow.selectedBudgetId)?.name}</strong></p>
                    
                    {serverSetupFlow.validationError && (
                      <div className="error">
                        <p>{getValidationErrorText(serverSetupFlow.validationError)}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>{t('budgetPassword', { ns: 'serverManagement' })}</label>
                    <input
                      type="password"
                      value={serverSetupFlow.budgetPassword || ''}
                      onChange={(e) => setServerSetupFlow(prev => ({ ...prev, budgetPassword: e.target.value, validationError: undefined }))}
                      placeholder={t('budgetPasswordPlaceholder', { ns: 'serverManagement' })}
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && serverSetupFlow.selectedBudgetId && serverSetupFlow.budgetPassword && selectedServerForManagement) {
                          const selectedServer = savedServers.find(s => s.id === selectedServerForManagement);
                          if (selectedServer) {
                            updateServerDefaultBudget(selectedServer, serverSetupFlow.selectedBudgetId, serverSetupFlow.budgetPassword);
                          }
                        }
                      }}
                    />
                    <p className="password-security-notice">{t('passwordSecurityNotice', { ns: 'common' })}</p>
                  </div>
                  
                  <div className="setup-actions">
                    <button 
                      onClick={() => setServerSetupFlow(prev => ({ ...prev, step: 'budget', needsBudgetPassword: false, budgetPassword: '', validationError: undefined }))}
                      className="back-button"
                    >
                      {t('backToBudgets', { ns: 'serverManagement' })}
                    </button>
                    <button 
                      onClick={() => {
                        if (serverSetupFlow.selectedBudgetId && serverSetupFlow.budgetPassword && selectedServerForManagement) {
                          const selectedServer = savedServers.find(s => s.id === selectedServerForManagement);
                          if (selectedServer) {
                            updateServerDefaultBudget(selectedServer, serverSetupFlow.selectedBudgetId, serverSetupFlow.budgetPassword);
                          }
                        }
                      }}
                      disabled={!serverSetupFlow.budgetPassword || isLoading}
                      className="unlock-budget-button"
                    >
                      {isLoading ? t('validating', { ns: 'serverManagement' }) : t('validateAndSetDefault', { ns: 'serverManagement' })}
                    </button>
                  </div>
                </div>
              )}

              {serverSetupFlow.step === 'validating' && (
                <div className="budget-validation-section">
                  <div className="validating-state">
                    <div className="spinner"></div>
                    <h5>{t('validatingBudgetAccess', { ns: 'serverManagement' })}</h5>
                    <p>{t('testingAccess', { ns: 'serverManagement' })} <strong>{serverSetupFlow.budgets.find(b => b.id === serverSetupFlow.selectedBudgetId)?.name}</strong></p>
                    <p>{t('verifyingBudget', { ns: 'serverManagement' })}</p>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
            );
          })()}

          {error && (
            <div className="error">
              <h4>{t('error', { ns: 'import' })}</h4>
              <p>{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServerManagementModal; 